import React, { lazy, useEffect, useState, useContext, Suspense } from 'react';

import { DashboardLayout } from '../../components/atoms';
import { Outlet } from 'react-router-dom';
import { useAuth } from '../../store/auth';
import { useTranslation } from 'react-i18next';
import { EnvService } from '../../services/EnvService';
import { ReactComponent as HomeIcon } from '../../assets/svg/home.svg';
import { ReactComponent as CompanyIcon } from '../../assets/svg/company.svg';
import { ReactComponent as MailIcon } from '../../assets/svg/mailroom.svg';
import { ReactComponent as SecretaryIcon } from '../../assets/svg/company-secretary.svg';
import { ReactComponent as AccountingIcon } from '../../assets/svg/accounting.svg';
import { ReactComponent as RequestsIcon } from '../../assets/svg/requests.svg';
import { ReactComponent as BillingIcon } from '../../assets/svg/billing-subscription.svg';
import { ReactComponent as EnquiryIcon } from '../../assets/svg/enquiry.svg';
import { ReactComponent as PreferencesIcon } from '../../assets/svg/preferences.svg';
import { FiCalendar, FiUsers, FiFileText, FiMessageSquare, FiSettings, FiDollarSign, FiHelpCircle, FiRefreshCw, FiUser } from 'react-icons/fi';
import { useSelector } from 'react-redux';
import { styleGuideSelector } from '../../store/globalSlice/styleSlice';
import { useAppSelector } from '../../hooks/hooksRedux';
import { DashboardCustomerProvider, DashboardCustomerContext } from './Context';

const DashboardAsideBar = lazy(() => import('../../components/DashboardTemplate/DashboardAsideBar'));
const DashboardHeader = lazy(() => import('../../components/DashboardTemplate/DashboardHeader'));

const DashboardCustomerContent: React.FC = () => {
  const { t } = useTranslation();

  // Adiciona a animação CSS para o efeito de piscada
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes blink {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0.3; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const envService = EnvService.MODULE_ACCOUNTING_TOGGLE;
  const isVibileAccountingModule = envService === 'ture' ? false : true;
  const { colors } = useSelector(styleGuideSelector);

  const { token, company } = useAuth();
  const { latestAnnualFee, refreshLatestAnnualFee } = useContext(DashboardCustomerContext);

  const [showAnnualRenewal, setShowAnnualRenewal] = useState<boolean>(false);

  const items = {
    initial: [
      {
        label: t('titles.ttlHome'),
        icon: <HomeIcon style={{ fill: colors.primary }} />,
        href: '/dashboard'
      }
    ],
    company: [
      {
        label: t('labels.lblCompanyDetails'),
        icon: <CompanyIcon style={{ fill: colors.primary }} />,
        href: '/dashboard/companyInfo'
      },
      {
        label: t('labels.lblContactUs'),
        icon: <FiMessageSquare color={colors.primary} size={24} />,
        href: '/dashboard/chat'
      },
      {
        label: t('labels.lblAnalyzeDocuments'),
        icon: <FiFileText color={colors.primary} size={24} />,
        href: '/dashboard/view-documents'
      },
      {
        label: t('labels.lblDirectorsAndShareholders'),
        icon: <FiUsers color={colors.primary} size={24} />,
        href: '/dashboard/directors-preferences'
      },
      {
        label: t('labels.lblCompanySecretary'),
        icon: <SecretaryIcon style={{ fill: colors.primary }} />,
        href: '/dashboard',
        showCommingSoon: true,
        isVisible: false
      },
      {
        label: t('labels.lblAccounting'),
        icon: <AccountingIcon style={{ fill: 'currentColor' }} />,
        href: '/dashboard/accounting',
        isVisible: isVibileAccountingModule,
        lookDisabled: true
      },
      {
        label: t('labels.lblRequests'),
        icon: <RequestsIcon style={{ fill: colors.primary }} />,
        href: '/dashboard/request'
      },
      {
        label: t('labels.lblBilling'),
        icon: <FiDollarSign color={colors.primary} size={24} />,
        href: '/dashboard/billing'
      },
      {
        label: t('labels.lblAnnualRenewal'),
        icon: (
          <div style={{
            animation: showAnnualRenewal ? 'blink 1s infinite' : 'none',
            display: 'flex',
            alignItems: 'center'
          }}>
            <FiCalendar color={colors.primary} size={24} />
          </div>
        ),
        href: '/dashboard/annual-renewal',
        isVisible: true,
      },
      {
        label: t('labels.lblEnquiry'),
        icon: <FiHelpCircle color={colors.primary} size={24} />,
        href: '/dashboard/enquiry'
      },
      {
        label: 'create-offshore',
        href: '/dashboard/create-offshore',
        isVisible: false
      }
    ],
    config: [
      {
        label: t('labels.lblPrefereces'),
        icon: <FiUser color={colors.primary} size={24} />,
        href: '/dashboard/preferences'
      }
    ]
  };

  const itemsCompanyNotSelected = {
    initial: [
      {
        label: t('titles.ttlHome'),
        icon: <HomeIcon style={{ fill: colors.primary }} />,
        href: '/dashboard'
      }
    ],
    company: [
      {
        label: t('labels.lblBilling'),
        icon: <FiDollarSign color={colors.primary} size={24} />,
        href: '/dashboard/billing',
        showCommingSoon: true
      },
      {
        label: 'Enquiry',
        icon: <FiHelpCircle color={colors.primary} size={24} />,
        href: '/dashboard/enquiry'
      }
    ],
    config: [
      {
        label: t('labels.lblPrefereces'),
        icon: <FiUser color={colors.primary} size={24} />,
        href: '/dashboard/preferences'
      },
      {
        label: t('labels.lblEnvironmentSetup'),
        icon: <FiSettings color={colors.primary} size={24} />,
        href: '/dashboard/environment-setup'
      }
    ]
  };

  const [dashboardItems, setDashboardItems] = useState<any>(null);

  useEffect(() => {
    setDashboardItems(company ? items : null);
  }, [company]);

  useEffect(() => {
    if (!company || !token) {
      setShowAnnualRenewal(false);
      return;
    }

    if (!latestAnnualFee) {
      void refreshLatestAnnualFee();
      return;
    }

    const dueDateStr = latestAnnualFee?.due_date;
    const status = latestAnnualFee?.status;

    if (!dueDateStr || status === 'paid') {
      setShowAnnualRenewal(false);
      return;
    }

    const dueDate = new Date(dueDateStr);
    const now = new Date();
    dueDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    const diffTime = dueDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const shouldShow = diffDays <= 90 && diffDays >= 0;
    setShowAnnualRenewal(shouldShow);
  }, [company, token, latestAnnualFee, refreshLatestAnnualFee]);

  return (
    <DashboardLayout
      headerComponent={<DashboardHeader />}
      sidebarComponent={
        <DashboardAsideBar items={dashboardItems === null ? itemsCompanyNotSelected : dashboardItems} />
      }
    >
      <Suspense fallback={<div className="p-6 text-center">Loading...</div>}>
        <Outlet />
      </Suspense>
    </DashboardLayout>
  );
};

const DashboardCustomer: React.FC = () => (
  <DashboardCustomerProvider>
    <DashboardCustomerContent />
  </DashboardCustomerProvider>
);

export { DashboardCustomer };
